import { zodResolver } from "@hookform/resolvers/zod";
import { validateEmails } from "@repo/core/helpers/zod.helpers";
import { proposalImplementationInvoiceDataFormSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { resolveCardDay } from "../hooks/useCreditProposal/mappers";
import useToast from "@repo/core/components/core/Toast";
import { TIPO_ENVIO_FATURA } from "@repo/core/services/entities/Proposals";
import type { ISelectOption } from "@repo/core/components/core/Select";

const mobileProposalResource = new MobileProposalResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);

type ProposalImplementationInvoiceDataFormType = z.infer<
  typeof proposalImplementationInvoiceDataFormSchema
>;

type InvoiceDeliveryOptionValue =
  ProposalImplementationInvoiceDataFormType["invoiceDelivery"];

const isAllowedInvoiceDelivery = (value?: boolean | string | null) => {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = value?.trim().toUpperCase();

  if (!normalized) {
    return false;
  }

  return !["N", "NAO", "NÃO", "FALSE", "0"].includes(normalized);
};

const normalizeInvoiceDelivery = (
  value: string | undefined,
  options: ISelectOption[]
): InvoiceDeliveryOptionValue => {
  const isSupportedDeliveryOption = options.some(
    (option) => option.value === value
  );

  return isSupportedDeliveryOption
    ? (value as InvoiceDeliveryOptionValue)
    : ((options[0]?.value ?? "") as InvoiceDeliveryOptionValue);
};

export const InvoiceDataFormController = () => {
  const { replace, completeStep, goToPreviousStep } =
    useProposalFlowNavigation();
  const toast = useToast();
  const {
    updateProposalData,
    proposalData,
    resetCreditProposalStore,
    persistFullProposalSnapshot
  } = useCreditProposal();

  const { data: expirationDates } = useQuery({
    queryKey: ["expirationDates", proposalData?.strProduto],
    queryFn: () =>
      mobileProposalResource.fetchInvoiceExpirationDates(
        proposalData?.strProduto || ""
      )
  });

  const {
    data: invoiceDeliveryTypes,
    isLoading: isLoadingInvoiceDeliveryOptions
  } = useQuery({
    queryKey: ["invoiceDeliveryTypes", proposalData?.strProduto],
    queryFn: () =>
      requisitionsResource.getInvoiceDeliveryTypes(
        proposalData?.strProduto || ""
      ),
    enabled: !!proposalData?.strProduto,
    select: (data) => data.data
  });

  const invoiceDeliveryOptions = useMemo(() => {
    if (!invoiceDeliveryTypes) {
      return [];
    }

    return [
      {
        label: "App",
        value: TIPO_ENVIO_FATURA.APP,
        isAllowed: true
      },
      {
        label: "E-mail",
        value: TIPO_ENVIO_FATURA.EMAIL,
        isAllowed: invoiceDeliveryTypes.permiteenviofaturaemail
      },
      {
        label: "SMS",
        value: TIPO_ENVIO_FATURA.SMS,
        isAllowed: invoiceDeliveryTypes.permiteenviofaturasms
      },
      {
        label: "Correio",
        value: TIPO_ENVIO_FATURA.POST_OFFICE,
        isAllowed: invoiceDeliveryTypes.permiteenviofaturacorreio
      }
    ]
      .filter((option) => isAllowedInvoiceDelivery(option.isAllowed))
      .map(({ label, value }) => ({ label, value }));
  }, [invoiceDeliveryTypes]);

  const invoiceFormValues = useMemo(() => {
    const cardDay = proposalData ? resolveCardDay(proposalData) : undefined;

    return {
      invoiceDelivery: normalizeInvoiceDelivery(
        proposalData?.strEnvioFatura,
        invoiceDeliveryOptions
      ),
      email: proposalData?.strEmail || "",
      confirmEmail: proposalData?.strEmail || "",
      invoiceDate: cardDay !== undefined ? String(cardDay) : ""
    };
  }, [invoiceDeliveryOptions, proposalData]);

  const invoiceDataFormSchema =
    proposalImplementationInvoiceDataFormSchema.superRefine(
      ({ email, confirmEmail, invoiceDelivery }, ctx) => {
        const hasEmailInput = Boolean(email || confirmEmail);
        const issues = validateEmails({
          email: email || "",
          confirmEmail: confirmEmail || "",
          receiveViaEmail:
            invoiceDelivery === TIPO_ENVIO_FATURA.EMAIL || hasEmailInput
        });
        issues.forEach((issue) => ctx.addIssue(issue));
      }
    );

  const { control, handleSubmit, setError, setValue, watch } =
    useForm<ProposalImplementationInvoiceDataFormType>({
      resolver: zodResolver(invoiceDataFormSchema),
      mode: "onSubmit",
      reValidateMode: "onChange",
      values: invoiceFormValues
    });

  useEffect(() => {
    const cardDay = proposalData ? resolveCardDay(proposalData) : undefined;

    if (cardDay !== undefined) {
      setValue("invoiceDate", String(cardDay), {
        shouldDirty: false,
        shouldValidate: false
      });
    }
  }, [proposalData, setValue]);

  const isEmailDelivery = watch("invoiceDelivery") === TIPO_ENVIO_FATURA.EMAIL;

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    const invoiceDate = formData.invoiceDate?.trim();

    if (!invoiceDate) {
      setError("invoiceDate", {
        type: "manual",
        message: "É necessário selecionar o dia de vencimento."
      });
      toast.error(
        "Selecione o dia da fatura",
        "Escolha um dia de vencimento para continuar."
      );
      return;
    }

    if (proposalData) {
      const email = formData.email?.trim() ?? "";

      updateProposalData({
        intDiaCartao: parseInt(invoiceDate, 10),
        strEnvioFatura: formData.invoiceDelivery,
        ...(email ? { strEmail: email, strInfoEmail: email } : {})
      });
    }

    try {
      await persistFullProposalSnapshot();
    } catch {
      toast.error(
        "Erro ao salvar etapa",
        "Não foi possível salvar os dados desta etapa. Tente novamente."
      );
      return;
    }

    completeStep(ROUTES.creditProposalPersonalReference);
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.creditProposalProfessionData);
  };

  const handleClose = () => {
    resetCreditProposalStore();
    replace(ROUTES.home);
  };

  const { expirationDateOptions } = useMemo(() => {
    const expirationDateOptions = expirationDates?.data.map((item) => {
      return {
        label: item.intDiaVencimento.toString(),
        value: item.intDiaVencimento.toString()
      };
    });
    return { expirationDateOptions };
  }, [expirationDates]);

  return {
    control,
    expirationDateOptions,
    invoiceDeliveryOptions,
    isLoadingInvoiceDeliveryOptions,
    isEmailDelivery,
    handleOnSubmitForm,
    handleGoBack,
    handleClose
  };
};
